const ELEMENT_NAME = 'ppl-access-point-widget'
const SCRIPT_ID = 'ppl-access-point-widget-loader'
const SUPPORTED_LANGUAGES = ['bg', 'cs', 'de', 'en', 'es', 'fr', 'hr', 'hu', 'it', 'nl', 'no', 'pl', 'ro', 'sk']

let loaderPromise

function loadWidget(loaderUrl) {
  if (window.customElements.get(ELEMENT_NAME)) {
    return Promise.resolve()
  }

  if (loaderPromise) {
    return loaderPromise
  }

  loaderPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(SCRIPT_ID)

    if (existingScript) {
      window.customElements.whenDefined(ELEMENT_NAME).then(resolve, reject)
      return
    }

    const script = document.createElement('script')
    script.async = true
    script.id = SCRIPT_ID
    script.src = loaderUrl

    script.addEventListener(
      'load',
      () => {
        window.customElements.whenDefined(ELEMENT_NAME).then(resolve, reject)
      },
      { once: true },
    )
    script.addEventListener(
      'error',
      () => {
        script.remove()
        loaderPromise = null
        reject(new Error(`Unable to load PPL Access Point Widget from ${loaderUrl}`))
      },
      { once: true },
    )

    document.body.appendChild(script)
  })

  return loaderPromise
}

export default {
  name: 'BranchSelectorPPL',
  props: {
    apiKey: {
      type: String,
      default: '',
    },
    shipper: {
      type: Object,
      required: true,
    },
    widgetConfig: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isLoaded: false,
      isOpen: false,
      isRendered: false,
      readyPromise: null,
      rejectReady: null,
      resolveReady: null,
    }
  },
  computed: {
    componentSettings() {
      return this.$themeSettings.components.BranchSelectorPPL || {}
    },
    country() {
      const countryObject = this.$store.state.globals.countries.find(
        ({ alpha3 }) => alpha3 === this.$store.state.cart.input.country,
      )

      return countryObject?.alpha2.toUpperCase()
    },
    loaderUrl() {
      return this.componentSettings.loaderUrl || 'https://www.ppl.cz/accesspointwidget/loader.js'
    },
    resolvedApiKey() {
      const settingKey = this.componentSettings.apiKeySetting || 'ppl_widget_api_key'

      return this.apiKey || this.$store.state.globals.settings?.integrations?.[settingKey] || ''
    },
    resolvedWidgetConfig() {
      return {
        viewMode: 'modal',
        defaultLang: this.widgetLanguage,
        ...(this.country ? { defaultCountry: this.country } : {}),
        ...(this.componentSettings.widgetConfig || {}),
        ...this.widgetConfig,
        // PPL Widget 2 does not reliably close from its built-in button.
        // A storefront-owned button calls the public close() API instead.
        closeButtonVisible: false,
      }
    },
    serializedWidgetConfig() {
      return JSON.stringify(this.resolvedWidgetConfig)
    },
    widgetLanguage() {
      const locale = this.$i18n.locale.split(/[-_]/)[0].toLowerCase()

      return SUPPORTED_LANGUAGES.includes(locale) ? locale : this.componentSettings.fallbackLanguage || 'en'
    },
  },
  methods: {
    async onBranchChange(event) {
      const branchId = this.processBranchId(event.detail)

      if (!branchId) {
        return
      }

      const branch = await this.$store.dispatch('cart/GET_STORE_BRANCH_BY_REMOTE_ID', {
        shipperId: this.shipper.id,
        remoteId: branchId,
      })

      if (!branch) {
        return
      }

      this.$store.commit('cart/SET_BRANCH', branch)
      this.$emit('input', event.detail)
      this.hide()
    },
    onWidgetError(event) {
      const error = new Error(event.detail?.message || 'PPL Access Point Widget failed to initialize')
      error.code = event.detail?.code

      if (this.rejectReady) {
        this.rejectReady(error)
        this.resetReadyPromise()
        return
      }

      this.$emit('error', error)
    },
    onWidgetClose() {
      this.isLoaded = false
      this.isOpen = false
      this.isRendered = false
      this.resetReadyPromise()
    },
    onWidgetReady() {
      this.isLoaded = true

      if (this.resolveReady) {
        this.resolveReady()
        this.resetReadyPromise()
      }

      this.$emit('init')
    },
    processBranchId(branch) {
      const code = branch?.code

      if (typeof code !== 'string') {
        return code
      }

      return this.componentSettings.stripBranchCode !== false ? code.replace(/^KM/i, '') : code
    },
    resetReadyPromise() {
      this.readyPromise = null
      this.rejectReady = null
      this.resolveReady = null
    },
    waitUntilReady() {
      if (this.isLoaded) {
        return Promise.resolve()
      }

      if (!this.readyPromise) {
        this.readyPromise = new Promise((resolve, reject) => {
          this.resolveReady = resolve
          this.rejectReady = reject
        })
      }

      return this.readyPromise
    },
    async open() {
      if (!this.resolvedApiKey) {
        this.$emit('error', new Error('PPL Access Point Widget API key is missing'))
        return
      }

      try {
        await loadWidget(this.loaderUrl)

        const readyPromise = this.waitUntilReady()
        this.isRendered = true
        await this.$nextTick()
        await readyPromise

        this.$refs.widget.open()
        this.isOpen = true
      } catch (error) {
        this.$emit('error', error)
      }
    },
    hide() {
      this.$refs.widget?.close()
      this.onWidgetClose()
    },
  },
}
